import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { trackPageView } from "./analytics";
import { DashboardPage } from "./pages/DashboardPage";
import { DataConsolePage } from "./pages/DataConsolePage";
import { EnsembleBuilderPage } from "./pages/EnsembleBuilderPage";
import { EvaluationPage } from "./pages/EvaluationPage";
import { FeatureLabPage } from "./pages/FeatureLabPage";
import { ResearchLogPage } from "./pages/ResearchLogPage";
import { SignalDecayPage } from "./pages/SignalDecayPage";
import { ScorecardPage } from "./pages/ScorecardPage";
import { IndexRotationPage } from "./pages/IndexRotationPage";
import { SuePeadPage } from "./pages/SuePeadPage";
import { MacroFactorsPage } from "./pages/MacroFactorsPage";
import { IndicesPage } from "./pages/IndicesPage";
import { InstrumentDetailPage } from "./pages/InstrumentDetailPage";
import { LandingPage } from "./pages/LandingPage";
import { MarketMythsPage } from "./pages/MarketMythsPage";
import { MarketPage } from "./pages/MarketPage";
import { CoveragePage } from "./pages/CoveragePage";
import { ModelBuilderPage } from "./pages/ModelBuilderPage";
import { ModelDetailPage } from "./pages/ModelDetailPage";
import { SimulationWorkbenchPage } from "./pages/SimulationWorkbenchPage";
import { ScheduledJobsPage } from "./pages/ScheduledJobsPage";
import { QueuesPage } from "./pages/QueuesPage";
import { StocksPage } from "./pages/StocksPage";
import { StrategySimulatorPage } from "./pages/StrategySimulatorPage";
import { UsersPage } from "./pages/UsersPage";
import { WorkingPaperPage } from "./pages/WorkingPaperPage";
import { ExecutionStackPage } from "./pages/ExecutionStackPage";
import { DailyDigestPage } from "./pages/DailyDigestPage";

type Route =
  | { view: "market" }
  | { view: "indices" }
  | { view: "stocks" }
  | { view: "detail"; securityId: string; from: "stocks" | "indices" }
  | { view: "evaluation" }
  | { view: "scorecard" }
  | { view: "dashboard" }
  | { view: "strategysim" }
  | { view: "model"; modelId: string; version: string; from: "evaluation" | "dashboard" | "featurelab" }
  | { view: "console" }
  | { view: "scheduledjobs" }
  | { view: "queues" }
  | { view: "users" }
  | { view: "ensembles" }
  | { view: "featurelab"; featureKey?: string }
  | { view: "researchlog" }
  | { view: "signaldecay" }
  | { view: "modelbuilder" }
  | { view: "coverage" }
  | { view: "executionstack" }
  | { view: "dailydigest" }
  | { view: "simulation"; securityId: string; from: "detail" | "market" };

function NavGroup({ label, active, items }: {
  label: string;
  active: boolean;
  items: { label: string; active: boolean; onClick: () => void }[];
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);

  function show() {
    window.clearTimeout(closeTimer.current);
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ left: r.left, top: r.bottom + 4 });
    setOpen(true);
  }
  function hideSoon() {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 140);
  }

  return (
    <div className="navgroup" onMouseEnter={show} onMouseLeave={hideSoon} onFocus={show} onBlur={hideSoon}>
      <button ref={btnRef} className={`app-nav-tab${active ? " active" : ""}`}>{label} ▾</button>
      {open && pos &&
        createPortal(
          <div
            className="navflyout"
            style={{ left: pos.left, top: pos.top }}
            onMouseEnter={show}
            onMouseLeave={hideSoon}
          >
            {items.map((it) => (
              <button
                key={it.label}
                className={`navflyout-item${it.active ? " active" : ""}`}
                onClick={() => { setOpen(false); it.onClick(); }}
              >
                {it.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

interface Session {
  token: string;
  role: string;
  userId: string;
}

const STORAGE_KEY = "tp.session";

function loadSession(): Session | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

/**
 * Phase 8: Market/Indices/Stocks + Instrument Detail, wired to real
 * endpoints. Login drops into Market (ui_spec U1: "no separate home page
 * ... Market tab as a rich state-of-the-market landing"). Models stays a
 * named-only placeholder -- not built this session, shown disabled rather
 * than a dead link pretending to work.
 */
export default function App() {
  const [session, setSession] = useState<Session | null>(loadSession());
  const [route, setRoute] = useState<Route>({ view: "market" });

  // GA4 SPA page-view tracking -- only the view name, never securityId/
  // modelId/etc (see analytics.ts). The pre-login LandingPage below gets
  // gtag.js's own default initial-load page_view; this covers the logged-
  // in app's in-memory route changes, which never touch the real URL.
  useEffect(() => {
    if (session) trackPageView(route.view);
  }, [session, route.view]);

  function onLoggedIn(token: string, role: string, userId: string) {
    const s = { token, role, userId };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    setSession(s);
  }

  function logout() {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
  }

  // Market Myths: Tested -- a public page reachable at /market-myths,
  // regardless of session state (checked before the login branch below).
  // Signed off public 2026-10-25: linked from the landing page nav, in
  // sitemap.xml, indexable. Grew from 4 to 10 cards on 2026-10-25 (the
  // Corroboration/Turnover/Momentum-5d/52-week-high/Phase-3-sweep/
  // FPI-cyclicity batch), then all 10 cards + 6 static evidence docs (now
  // bespoke components dispatched from inside MarketMythsPage.tsx) + the
  // 3 living articles below were rewritten into a humanized voice on
  // 2026-10-28, reviewed on an isolated preview build, then flipped here.
  if (window.location.pathname.startsWith("/market-myths")) {
    return <MarketMythsPage />;
  }

  // Working Paper -- same public, pre-auth pattern as Market Myths. DEPLOYED
  // UNLINKED pending review (2026-10-26): no nav link anywhere in this app,
  // not in sitemap.xml -- reachable only by typing the URL. The page's own
  // noindex meta (set on mount, see WorkingPaperPage.tsx) keeps it out of
  // search regardless.
  if (window.location.pathname.startsWith("/working-paper")) {
    return <WorkingPaperPage />;
  }

  // Index rotation -- the first "living" (scheduled-refresh) Market Myths
  // article, same public pre-auth pattern. DEPLOYED UNLINKED pending
  // review (2026-10-25): no nav link anywhere, not in sitemap.xml -- the
  // page's own noindex meta (set on mount, see IndexRotationPage.tsx)
  // keeps it out of search regardless.
  if (window.location.pathname.startsWith("/index-rotation")) {
    return <IndexRotationPage />;
  }

  // PEAD -- the second "living" Market Myths article, same public pre-auth
  // pattern. DEPLOYED UNLINKED pending review (2026-10-27): no nav link
  // anywhere, not in sitemap.xml -- the page's own noindex meta (set on
  // mount, see SuePeadPage.tsx) keeps it out of search regardless.
  if (window.location.pathname.startsWith("/sue-pead")) {
    return <SuePeadPage />;
  }

  // Macro factors -- the third "living" Market Myths article, same public
  // pre-auth pattern. DEPLOYED UNLINKED pending review (2026-10-27): no
  // nav link anywhere, not in sitemap.xml -- the page's own noindex meta
  // (set on mount, see MacroFactorsPage.tsx) keeps it out of search
  // regardless.
  if (window.location.pathname.startsWith("/macro-factors")) {
    return <MacroFactorsPage />;
  }

  if (!session) {
    // Public landing page (route "/", Phase 8) -- the front door. Its own
    // "Log in" tab is a real, working login form (auth already exists);
    // "Request invite" renders but its capture is Phase 10 (needs the
    // invite-link/email dependency).
    return <LandingPage onLoggedIn={onLoggedIn} />;
  }

  return (
    <>
      <header className="app-topnav">
        <div className="app-topnav-in">
          <div className="app-brand">
            <span className="dot" />
            TICKERPICKER
          </div>
          <nav className="app-nav-tabs">
            <button className={`app-nav-tab${route.view === "market" ? " active" : ""}`} onClick={() => setRoute({ view: "market" })}>
              Market
            </button>
            <button className={`app-nav-tab${route.view === "indices" ? " active" : ""}`} onClick={() => setRoute({ view: "indices" })}>
              Indices
            </button>
            <button
              className={`app-nav-tab${route.view === "stocks" || route.view === "detail" ? " active" : ""}`}
              onClick={() => setRoute({ view: "stocks" })}
            >
              Stocks
            </button>
            <NavGroup
              label="Model"
              active={route.view === "dashboard" || route.view === "modelbuilder" || route.view === "evaluation" || route.view === "scorecard" || (route.view === "model" && (route.from === "dashboard" || route.from === "evaluation"))}
              items={[
                { label: "Model Overview", active: route.view === "dashboard" || (route.view === "model" && route.from === "dashboard"), onClick: () => setRoute({ view: "dashboard" }) },
                { label: "Evaluation & Champions", active: route.view === "evaluation" || (route.view === "model" && route.from === "evaluation"), onClick: () => setRoute({ view: "evaluation" }) },
                { label: "Scorecard", active: route.view === "scorecard", onClick: () => setRoute({ view: "scorecard" }) },
                { label: "Model Builder", active: route.view === "modelbuilder", onClick: () => setRoute({ view: "modelbuilder" }) },
              ]}
            />
            <button
              className={`app-nav-tab${route.view === "strategysim" ? " active" : ""}`}
              onClick={() => setRoute({ view: "strategysim" })}
            >
              Simulator
            </button>
          </nav>
          {session.role === "admin" && (
            <nav className="app-nav-tabs" style={{ marginLeft: 10, paddingLeft: 10, borderLeft: "1px solid var(--rule-strong)" }}>
              <NavGroup
                label="Research"
                active={route.view === "featurelab" || route.view === "researchlog" || route.view === "signaldecay"}
                items={[
                  { label: "Feature Lab", active: route.view === "featurelab", onClick: () => setRoute({ view: "featurelab" }) },
                  { label: "Research Log", active: route.view === "researchlog", onClick: () => setRoute({ view: "researchlog" }) },
                  { label: "Signal Half-Life", active: route.view === "signaldecay", onClick: () => setRoute({ view: "signaldecay" }) },
                ]}
              />
              <button className={`app-nav-tab${route.view === "coverage" ? " active" : ""}`} onClick={() => setRoute({ view: "coverage" })}>
                Coverage
              </button>
              <NavGroup
                label="Operations"
                active={route.view === "console" || route.view === "scheduledjobs" || route.view === "queues"}
                items={[
                  { label: "Data ops", active: route.view === "console", onClick: () => setRoute({ view: "console" }) },
                  { label: "Scheduled jobs", active: route.view === "scheduledjobs", onClick: () => setRoute({ view: "scheduledjobs" }) },
                  { label: "Queues", active: route.view === "queues", onClick: () => setRoute({ view: "queues" }) },
                ]}
              />
              <button className={`app-nav-tab${route.view === "users" ? " active" : ""}`} onClick={() => setRoute({ view: "users" })}>
                Users
              </button>
              <button className={`app-nav-tab${route.view === "executionstack" ? " active" : ""}`} onClick={() => setRoute({ view: "executionstack" })}>
                Execution Stack
              </button>
              <button className={`app-nav-tab${route.view === "dailydigest" ? " active" : ""}`} onClick={() => setRoute({ view: "dailydigest" })}>
                Daily Digest
              </button>
            </nav>
          )}
          <div className="app-shell-right">
            <div className="who">
              <span className="role-badge">{session.role}</span>
            </div>
            <button className="logout-btn" onClick={logout}>
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="page">
      <ErrorBoundary resetKey={route.view}>
        {route.view === "market" && (
          <MarketPage token={session.token} onUnauthorized={logout} onNavigateToIndices={() => setRoute({ view: "indices" })} />
        )}
        {route.view === "indices" && (
          <IndicesPage
            token={session.token}
            onUnauthorized={logout}
            onSelectIndex={(securityId) => setRoute({ view: "detail", securityId, from: "indices" })}
          />
        )}
        {route.view === "stocks" && (
          <StocksPage
            token={session.token}
            onUnauthorized={logout}
            onSelectStock={(securityId) => setRoute({ view: "detail", securityId, from: "stocks" })}
          />
        )}
        {route.view === "detail" && (
          <InstrumentDetailPage
            token={session.token}
            securityId={route.securityId}
            onBack={() => setRoute({ view: route.from })}
            onUnauthorized={logout}
            // The Simulation Workbench is stock-only (an index has its own
            // separate, already-wired read-only MC on the Market page's
            // Forward View, not this admin-gated stock endpoint) -- omitted
            // when arriving from Indices so the button renders honestly
            // disabled (InstrumentDetailPage's own `disabled={!onSimulate}`)
            // rather than wiring an action that would 400 on an index.
            onSimulate={
              route.from === "stocks"
                ? (securityId) => setRoute({ view: "simulation", securityId, from: "detail" })
                : undefined
            }
          />
        )}
        {route.view === "simulation" && (
          <SimulationWorkbenchPage
            token={session.token}
            securityId={route.securityId}
            isAdmin={session.role === "admin"}
            onUnauthorized={logout}
            // Simulation is only ever reached from a "detail" view whose own
            // onSimulate was wired (only when that detail page's `from` was
            // "stocks" -- an index-originated detail view never gets an
            // onSimulate at all), so "stocks" is always the correct `from`
            // to hand back here.
            onBack={() => setRoute(route.from === "detail" ? { view: "detail", securityId: route.securityId, from: "stocks" } : { view: "market" })}
          />
        )}
        {route.view === "evaluation" && (
          <EvaluationPage
            token={session.token}
            onUnauthorized={logout}
            onSelectModel={(modelId, version) => setRoute({ view: "model", modelId, version, from: "evaluation" })}
          />
        )}
        {route.view === "scorecard" && <ScorecardPage token={session.token} onUnauthorized={logout} />}
        {route.view === "dashboard" && (
          <DashboardPage
            token={session.token}
            onUnauthorized={logout}
            onSelectModel={(modelId, version) => setRoute({ view: "model", modelId, version, from: "dashboard" })}
          />
        )}
        {route.view === "model" && (
          <ModelDetailPage
            token={session.token}
            modelId={route.modelId}
            version={route.version}
            onUnauthorized={logout}
            onBack={() => setRoute({ view: route.from })}
            onOpenFeature={(featureKey) => setRoute({ view: "featurelab", featureKey })}
            isAdmin={session.role === "admin"}
          />
        )}
        {route.view === "strategysim" && <StrategySimulatorPage token={session.token} onUnauthorized={logout} />}
        {route.view === "featurelab" && (
          <FeatureLabPage
            token={session.token}
            initialFeatureKey={route.featureKey}
            onUnauthorized={logout}
            onSelectModel={(modelId, version) => setRoute({ view: "model", modelId, version, from: "featurelab" })}
          />
        )}
        {route.view === "researchlog" && <ResearchLogPage token={session.token} onUnauthorized={logout} />}
        {route.view === "signaldecay" && <SignalDecayPage token={session.token} onUnauthorized={logout} />}
        {route.view === "modelbuilder" && <ModelBuilderPage token={session.token} onUnauthorized={logout} />}
        {route.view === "ensembles" && <EnsembleBuilderPage token={session.token} onUnauthorized={logout} />}
        {route.view === "coverage" && <CoveragePage token={session.token} onUnauthorized={logout} />}
        {route.view === "console" && <DataConsolePage token={session.token} onUnauthorized={logout} />}
        {route.view === "scheduledjobs" && <ScheduledJobsPage token={session.token} onUnauthorized={logout} />}
        {route.view === "queues" && <QueuesPage token={session.token} onUnauthorized={logout} />}
        {route.view === "users" && <UsersPage token={session.token} onUnauthorized={logout} currentUserId={session.userId} />}
        {route.view === "executionstack" && <ExecutionStackPage token={session.token} onUnauthorized={logout} />}
        {route.view === "dailydigest" && <DailyDigestPage token={session.token} onUnauthorized={logout} />}
      </ErrorBoundary>
      </main>
    </>
  );
}
